// src/pages/MentionsPage.js
// Uploaded file (if needed): sandbox:/mnt/data/alertRoutes.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Pagination,
  Stack,
  Chip,
  Button,
  LinearProgress
} from '@mui/material';
import { MentionCard } from '../components/MentionCard';
import { api } from '../api';
import debounce from 'lodash.debounce';

const DEFAULT_PAGE_SIZE = 20;
const TOP_KEYWORDS_LIMIT = 20;

const MentionsPage = ({ state, onGenerateReply }) => {
  // Filters
  const [filters, setFilters] = useState({
    keywordId: 'all',
    platform: 'all',
    sentiment: 'all',
    startDate: '',
    endDate: ''
  });

  // Server-side pagination & results
  const [mentions, setMentions] = useState([]); // accumulated mentions for infinite scroll
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // IntersectionObserver sentinel ref
  const sentinelRef = useRef(null);

  // local derived values
  const platformOptions = useMemo(() => [...new Set((state.mentions || []).map(m => m.platform))], [state.mentions]);

  // Build query params from filters + pagination
  const buildQuery = (p, ps, f) => {
    const params = { page: p, pageSize: ps };
    if (f.keywordId && f.keywordId !== 'all') params.keywordId = f.keywordId;
    if (f.platform && f.platform !== 'all') params.platform = f.platform;
    if (f.sentiment && f.sentiment !== 'all') params.sentiment = f.sentiment;
    if (f.startDate) params.startDate = f.startDate;
    if (f.endDate) params.endDate = f.endDate;
    return params;
  };

  // Fetch one page from server
  const fetchPage = useCallback(async (p = 1, ps = pageSize, f = filters) => {
    try {
      if (p === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const params = buildQuery(p, ps, f);
      const resp = await api.get('/mentions/paginated', { params });
      const payload = resp.data || {};
      const pageMentions = payload.mentions || payload.rows || [];
      const totalCount = payload.total ?? payload.count ?? 0;

      return { pageMentions, totalCount };
    } catch (err) {
      console.error('Error fetching mentions:', err);
      setError('Failed to load mentions from server.');
      return { pageMentions: [], totalCount: 0 };
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pageSize, filters]);

  // Load initial page or when filters/pageSize change
  const loadInitial = useCallback(async (p = 1, ps = pageSize, f = filters) => {
    setPage(p);
    setHasMore(true);
    setMentions([]);
    setTotal(0);

    const { pageMentions, totalCount } = await fetchPage(p, ps, f);
    setMentions(pageMentions);
    setTotal(totalCount);
    setHasMore(pageMentions.length + ( (p - 1) * ps ) < totalCount);
    if ((p === 1) && pageMentions.length < ps && totalCount <= pageMentions.length) setHasMore(false);
  }, [fetchPage]);

  // Debounced load to prevent rapid calls when filters change
  const debouncedLoad = useMemo(() => debounce((p, ps, f) => loadInitial(p, ps, f), 300), [loadInitial]);

  // When filters or pageSize change -> reset page 1 and load
  useEffect(() => {
    setPage(1);
    debouncedLoad(1, pageSize, filters);
    return () => debouncedLoad.cancel();
  }, [filters, pageSize, debouncedLoad]);

  // Infinite scroll: load next page when sentinel visible
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!hasMore) return;

    const observer = new IntersectionObserver(async (entries) => {
      const first = entries[0];
      if (first.isIntersecting && !loadingMore && !loading) {
        const nextPage = page + 1;
        setPage(nextPage);
        const { pageMentions, totalCount } = await fetchPage(nextPage, pageSize, filters);
        setMentions(prev => [...prev, ...pageMentions]);
        setTotal(totalCount);
        const loaded = (nextPage - 1) * pageSize + pageMentions.length;
        setHasMore(loaded < totalCount);
      }
    }, { root: null, rootMargin: '400px', threshold: 0.1 });

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [sentinelRef.current, page, pageSize, filters, fetchPage, loadingMore, hasMore, loading]);

  // Manual page jump handler
  const handlePageChange = async (e, value) => {
    setPage(value);
    const { pageMentions, totalCount } = await fetchPage(value, pageSize, filters);
    setMentions(pageMentions);
    setTotal(totalCount);
    setHasMore((value * pageSize) < totalCount);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Filters handler (local)
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Page size change handler
  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
  };

  // Derived values
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  // ---------------------------
  // Topic Cloud: frequency bars
  // Compute top keywords from currently loaded mentions (or optionally from server)
  // We'll compute frequencies by keywordId and also use state.keywords to show term
  // ---------------------------
  const topKeywords = useMemo(() => {
    const freq = {};
    (mentions || []).forEach(m => {
      const kid = m.keywordId ?? 'unknown';
      freq[kid] = (freq[kid] || 0) + 1;
    });
    const items = Object.keys(freq).map(kid => {
      const kw = (state.keywords || []).find(x => x.id === Number(kid));
      return { id: kid, term: kw ? kw.term : (kid === 'unknown' ? 'Unknown' : String(kid)), count: freq[kid] };
    }).sort((a,b) => b.count - a.count);
    return items.slice(0, TOP_KEYWORDS_LIMIT);
  }, [mentions, state.keywords]);

  // Click on a keyword bar -> set filter
  const handleKeywordClick = (kid) => {
    // If user clicks the same keyword, toggle off
    if (String(filters.keywordId) === String(kid)) {
      setFilters(prev => ({ ...prev, keywordId: 'all' }));
    } else {
      setFilters(prev => ({ ...prev, keywordId: Number(kid) }));
    }
    // page resets via effect
  };

  // Helper to compute max count (for bar width)
  const maxKeywordCount = useMemo(() => {
    return topKeywords.length ? Math.max(...topKeywords.map(k => k.count)) : 1;
  }, [topKeywords]);

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>Mentions</Typography>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Keyword</InputLabel>
                <Select name="keywordId" value={filters.keywordId} onChange={handleFilterChange} label="Keyword">
                  <MenuItem value="all">All Keywords</MenuItem>
                  {(state.keywords || []).map(kw => (
                    <MenuItem key={kw.id} value={kw.id}>{kw.term}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Platform</InputLabel>
                <Select name="platform" value={filters.platform} onChange={handleFilterChange} label="Platform">
                  <MenuItem value="all">All Platforms</MenuItem>
                  {platformOptions.map(p => (
                    <MenuItem key={p} value={p}>{p?.charAt(0)?.toUpperCase() + p?.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Sentiment</InputLabel>
                <Select name="sentiment" value={filters.sentiment} onChange={handleFilterChange} label="Sentiment">
                  <MenuItem value="all">All Sentiments</MenuItem>
                  <MenuItem value="Positive">Positive</MenuItem>
                  <MenuItem value="Negative">Negative</MenuItem>
                  <MenuItem value="Neutral">Neutral</MenuItem>
                  <MenuItem value="Unanalyzed">Unanalyzed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.25}>
              <TextField name="startDate" value={filters.startDate} onChange={handleFilterChange} type="date" label="Start Date" InputLabelProps={{ shrink: true }} fullWidth size="small" />
            </Grid>

            <Grid item xs={12} sm={6} md={2.25}>
              <TextField name="endDate" value={filters.endDate} onChange={handleFilterChange} type="date" label="End Date" InputLabelProps={{ shrink: true }} fullWidth size="small" />
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>Per page</InputLabel>
                <Select value={pageSize} onChange={handlePageSizeChange} label="Per page">
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <Button variant="outlined" onClick={() => {
                setFilters({ keywordId: 'all', platform: 'all', sentiment: 'all', startDate: '', endDate: '' });
              }}>Reset</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Grid */}
      <Grid container spacing={3}>
        {/* Left: mentions list */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">{total} Mentions Found</Typography>
                <Typography variant="body2" color="text.secondary">Showing {showingFrom}-{showingTo} of {total}</Typography>
              </Stack>

              {/* mentions list */}
              <Box>
                {mentions.map(m => (
                  <MentionCard key={m.id || m.postUrl} mention={m} onGenerateReply={onGenerateReply} />
                ))}
              </Box>

              {/* initial loading */}
              {loading && (
                <Box display="flex" justifyContent="center" mt={2}><CircularProgress /></Box>
              )}

              {/* loading more */}
              {loadingMore && (
                <Box display="flex" justifyContent="center" mt={2}><CircularProgress size={24} /></Box>
              )}

              {/* sentinel for infinite scroll */}
              <div ref={sentinelRef} style={{ height: 1 }} />

              {/* end or error */}
              {!hasMore && !loading && !loadingMore && total > 0 && (
                <Box textAlign="center" py={2}><Typography variant="body2" color="text.secondary">— End of results —</Typography></Box>
              )}

              {error && (<Box textAlign="center" py={2}><Typography color="error">{error}</Typography></Box>)}
            </CardContent>

            {/* manual pagination footer */}
            <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Pagination count={pageCount} page={page} onChange={handlePageChange} color="primary" />
                <Typography variant="caption" color="text.secondary">Page {page} / {pageCount}</Typography>
              </Stack>
            </Box>
          </Card>
        </Grid>

       </Grid>
    </Container>
  );
};

export default MentionsPage;
